// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import PropTypes from "prop-types";

import { Loader, TiVoTargetsSummary } from "components/elements";
import { Table, TableBody, TableRow, TableRowColumn } from "components/layout";
import { TextOverflowPopup, Tooltip } from "components/popups";
import { HtmlTranslation, trans } from "src/translations";
import { snakeToCamelCase } from "utils/string";

import { FIELD_DESCRIPTION, FIELDS } from "../../ManifestForm/constants";
import { ManifestPreviewModal } from "./ManifestPreviewModal";
import styles from "./ManifestPropertiesTable.scss";
import { ManifestUserScript } from "./ManifestUserScript";

const valuePrefix = (key, value) =>
  key === FIELDS.key_remapping.name ? `key '${value}'` : value;

function renderKey(key) {
  const transFunc = FIELD_DESCRIPTION[key];
  if (typeof transFunc === "function") {
    return (
      <>
        {`${key} `}
        <Tooltip
          content={<HtmlTranslation transFunc={transFunc} component="span" />}
        >
          (?)
        </Tooltip>
      </>
    );
  }
  return key;
}

function renderValue(value, key) {
  let displayValue = value;

  if (FIELDS[key]?.type === "select") {
    displayValue =
      FIELDS[key].values.find((option) => option.value === `${value}`)?.label ||
      displayValue;
  }

  switch (key) {
    case FIELDS.user_script.name:
      return <ManifestUserScript value={displayValue} />;
    case FIELDS.app_icon.name:
      return (
        <ManifestPreviewModal
          title={trans.APP_DETAILS_MANIFEST__DISPLAY_IMG_TITLE()}
        >
          <img src={displayValue} className={styles.imagePreview} />
        </ManifestPreviewModal>
      );
    case "targets":
      return <TiVoTargetsSummary targets={displayValue} />;
    default:
      return <TextOverflowPopup>{displayValue}</TextOverflowPopup>;
  }
}

function renderRow(key, value, description) {
  return (
    <TableRow key={`prop-${key}`}>
      <TableRowColumn columnClass={styles.nameRow}>
        {renderKey(snakeToCamelCase(key))}
      </TableRowColumn>
      <TableRowColumn colspan={description ? 1 : 2}>
        {renderValue(value, key)}
      </TableRowColumn>
      {description && <TableRowColumn>{description}</TableRowColumn>}
    </TableRow>
  );
}

function renderRows(attributes = {}, deleted) {
  const dataKeys = getAttributesKeys(attributes);

  if (deleted) {
    return (
      <TableRow>
        <TableRowColumn className={styles.manifestRemoved}>
          {trans.APP_DETAILS_MANIFEST__CONFIG_REMOVED()}
        </TableRowColumn>
      </TableRow>
    );
  }

  if (dataKeys.length === 0) {
    return (
      <TableRow>
        <TableRowColumn>
          {trans.APP_DETAILS_MANIFEST__CONFIG_NO_DATA()}
        </TableRowColumn>
      </TableRow>
    );
  }

  return dataKeys.map((key) => {
    const value = attributes[key];
    if (Array.isArray(value)) {
      return value.map((item) =>
        renderRow(valuePrefix(key, item.key), item.value, item.description)
      );
    }

    return renderRow(key, value);
  });
}

export function getAttributesKeys(attribiutes = {}) {
  return Object.keys(attribiutes).filter((key) =>
    Array.isArray(attribiutes[key])
      ? attribiutes[key].length > 0
      : attribiutes[key] !== null
  );
}

export function ManifestPropertiesTable({ attributes, deleted, loading }) {
  if (loading) {
    return <Loader />;
  }

  return (
    <Table className={styles.summaryTable} layout="fixed">
      <TableBody>{renderRows(attributes, deleted)}</TableBody>
    </Table>
  );
}

ManifestPropertiesTable.propTypes = {
  attributes: PropTypes.object.isRequired,
  deleted: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
};
